import {
    AppConfig,
    DealSelectorVariant,
    JourneyVariant,
    PricingTableConfig,
} from '@r6digital/rapidstor-types/react/entities/Journey';

// eslint-disable-next-line import/no-extraneous-dependencies
import {
    BrowserClient,
    defaultStackParser,
    getDefaultIntegrations,
    makeFetchTransport,
    Scope,
} from '@sentry/browser';
import * as Sentry from '@sentry/react';
import { RapidStorOptions } from '@/types';
import { sanitiseQuickSizeFilters, sanitiseUnitFilters } from './unitFilters';

/**
* Simple method to check if the current environment is development
*/
export const isDevelopment = () => import.meta.env.MODE !== 'production';

/**
* Simple method to check if the current environment is production
*/
export const isProduction = () => import.meta.env.MODE === 'production';

export const loggingClient = (): BrowserClient => {
    const integrations = getDefaultIntegrations({}).filter(
        (defaultIntegration) => !['BrowserApiErrors', 'Breadcrumbs', 'GlobalHandlers'].includes(
            defaultIntegration.name,
        ),
    );

    integrations.push(Sentry.browserTracingIntegration());
    integrations.push(Sentry.browserProfilingIntegration());

    const client = new BrowserClient({
        dsn: 'https://4f277693ee15aec89d6b09461e94bd74@o1132036.ingest.sentry.io/4506109304307712',
        transport: makeFetchTransport,
        stackParser: defaultStackParser,
        integrations,
        release: `rapidstor-v2-frontend@${__APP_VERSION__}`,
        environment: isDevelopment() ? 'development' : 'production',
    });

    return client;
};

export const loggingScope = (): Scope => {
    const client = loggingClient();
    const scope = new Scope();
    scope.setClient(client);

    return scope;
};

export const buildRapidStorOptions = (
    appConfigParsed: AppConfig,
    personaAttr?: string | null,
    moveInUrlAttr?: string | null,
): RapidStorOptions => {
    const defaultPricingTableOptions: PricingTableConfig = {
        showTaxRow: true,
        style: 'horizontal',
    };

    const allowedDealSelectorVariants: Set<DealSelectorVariant> = new Set([
        'ribbon',
    ]);
    const rawDealSelectorVariant = appConfigParsed.journey.dealSelectorVariant;

    const baseUrl = () => {
        if (appConfigParsed.journey.hosted) {
            return '/';
        }
        if (appConfigParsed.journey.dev) {
            return import.meta.env.VITE_DEV_BASE_URL ?? 'https://api.rapidstorapp.com';
        }
        return 'https://prod.rapidstorapp.com';
    };

    return {
        baseURL: baseUrl(),
        corpToken: appConfigParsed.journey.corpToken,
        corpCode: appConfigParsed.journey.corpCode,
        locationCode: appConfigParsed.journey.locationCode,
        location: undefined,
        hiddenLocations: [],
        startPosition: appConfigParsed.journey.startPosition
            ? `${appConfigParsed.journey.startPosition}`
            : undefined,
        persona: appConfigParsed.journey.persona || personaAttr || undefined,
        dev: appConfigParsed.journey.dev === true,
        moveInUrl: moveInUrlAttr ?? undefined,
        unitTypeNames:
            appConfigParsed.journey.unitTypeNames?.split(',') || undefined,
        unitTypeRegex: appConfigParsed.journey.unitTypeRegex || undefined,
        loqate: appConfigParsed.journey.loqate || undefined,
        language: appConfigParsed.journey.language || 'en',
        configSelector: appConfigParsed.journey.configSelector || false,
        agentDetails: appConfigParsed.journey.agentDetails || undefined,
        optionalMoveInFields: appConfigParsed.journey.optionalMoveInFields || [],
        pricingTable: appConfigParsed.journey.pricingTable
            ? {
                showTaxRow: true,
                style: 'horizontal',
                ...appConfigParsed.journey.pricingTable,
            }
            : defaultPricingTableOptions,
        showArrowsOnDescriptorMobile:
            appConfigParsed.journey.showArrowsOnDescriptorMobile || false,
        enabledCarouselEndCTA: appConfigParsed.journey.enabledCarouselEndCTA || false,
        variant: appConfigParsed.journey.variant || JourneyVariant.JBL,
        dialCodeSelector: appConfigParsed.journey.dialCodeSelector || false,
        alwaysShowCompanyFields: appConfigParsed.journey.alwaysShowCompanyFields || false,
        dealSelectorVariant: rawDealSelectorVariant && allowedDealSelectorVariants.has(rawDealSelectorVariant)
            ? rawDealSelectorVariant
            : undefined,
        checkoutVariant: appConfigParsed.journey.checkoutVariant ?? 'standard',
        unitFilters: sanitiseUnitFilters(appConfigParsed.journey.unitFilters),
        quickSizeFilters: sanitiseQuickSizeFilters(appConfigParsed.journey.quickSizeFilters),
    };
};

/**
 * Fetch the DB translation override chain (language < corporation < location)
 * for the current corp/site/language from Automate. Called after the app has
 * started; the result is merged into i18next beneath the snippet overrides.
 * Resilient — returns null on any failure so the running app is never disrupted.
 * Prefers the root-element attributes (the Automate site code) over
 * journey.locationCode, which may hold a resolved SiteLink code for multisite.
 */
export const fetchTranslationChain = async (
    language?: string,
): Promise<Record<string, string> | null> => {
    const root = document.getElementById('rapidstor-v2-frontend');
    const journey = window.rapidstor_settings?.app_config?.journey;
    const corp = root?.getAttribute('data-corpCode') ?? journey?.corpCode;
    const site = root?.getAttribute('data-locationCode') ?? journey?.locationCode;
    const automateUrlOverride = root?.getAttribute('data-automateBaseUrl');
    const lang = language || journey?.language || 'en';

    if (!corp || !site) return null;

    try {
        const automateBaseUrl = automateUrlOverride ??
            import.meta.env.VITE_AUTOMATE_BASE_URL ??
            'https://app.r6automate.com';
        const params = new URLSearchParams({ corp, site, language: lang });
        const res = await fetch(
            `${automateBaseUrl}/api/v1/tenant-journey/translations?${params.toString()}`,
        );
        if (!res.ok) return null;
        const json = await res.json();
        const chain = json?.data?.translations;
        return chain && typeof chain === 'object' ? chain : null;
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[RapidStor] Failed to fetch translations', e);
        return null;
    }
};

export interface RemoteConfigBody {
    corp: string;
    site: string;
    type: 'api' | 'prod';
    uuid: string;
    /** From data-journeyOverride: serve this journey config in place of the site's active one. */
    journey_override?: string;
}

/**
 * The tenant-journey/config request body for a remoteConfig embed. Automate
 * ignores a journey_override it cannot find and serves the site's journey.
 */
export const buildRemoteConfigBody = (
    root: HTMLElement | null,
    {
        corpCode, locationCode, dev, userUuid,
    }: {
        corpCode: string;
        locationCode: string;
        dev: boolean;
        userUuid: string;
    },
): RemoteConfigBody => {
    const journeyOverride = root?.getAttribute('data-journeyOverride')?.trim();

    return {
        corp: corpCode,
        site: locationCode,
        type: dev ? 'api' : 'prod',
        uuid: userUuid,
        ...(journeyOverride ? { journey_override: journeyOverride } : {}),
    };
};

const bootstrapApplicationConfig = async () => {
    const root = document.getElementById('rapidstor-v2-frontend');
    const persona = root?.getAttribute('data-persona');
    const moveInUrl = root?.getAttribute('data-moveInUrl');
    const automateBaseUrl = root?.getAttribute('data-automateBaseUrl');
    const corpCode = root?.getAttribute('data-corpCode');
    const locationCode = root?.getAttribute('data-locationCode');
    const remoteConfig = root?.hasAttribute('remoteConfig') ?? false;
    const dev = root?.hasAttribute('data-dev') ?? false;

    let userUuid = localStorage.getItem('rapidstor-user-uuid');
    if (!userUuid) {
        userUuid = crypto.randomUUID();
        localStorage.setItem('rapidstor-user-uuid', userUuid);
    }

    if (remoteConfig) {
        if (!corpCode || !locationCode) {
            throw new Error(
                'CorpCode and LocationCode are required for remote config',
            );
        }

        const automateBaseURL = automateBaseUrl ??
            import.meta.env.VITE_AUTOMATE_BASE_URL ??
            'https://app.r6automate.com';

        const result = await fetch(
            `${automateBaseURL}/api/v1/tenant-journey/config`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(buildRemoteConfigBody(root, {
                    corpCode,
                    locationCode,
                    dev,
                    userUuid,
                })),
            },
        );

        if (!result.ok) {
            throw new Error(
                `Failed to fetch tenant journey config: ${result.statusText}`,
            );
        }

        const response = await result.json();
        const config = response.data.data;

        const configUuid = localStorage.getItem('rapidstor-config-uuid');
        if (!configUuid) {
            localStorage.setItem('rapidstor-config-uuid', response.data.id);
        } else if (configUuid !== response.data.id) {
            localStorage.setItem('rapidstor-config-uuid', response.data.id);
        }

        root?.setAttribute('data-appConfig', JSON.stringify(config));
    }

    const appConfigAttr = root?.getAttribute('data-appConfig');
    if (!appConfigAttr) {
        throw new Error('No appConfig found on root element');
    }

    let appConfigParsed = JSON.parse(appConfigAttr) as AppConfig;

    // Apply dev override from localStorage if present and the snippet is in dev mode
    try {
        if (appConfigParsed.journey.dev) {
            const overrideRaw = localStorage.getItem(
                'rapidstor-dev-app-config-override',
            );
            if (overrideRaw) {
                const overrideJson = JSON.parse(overrideRaw);
                if (overrideJson && typeof overrideJson === 'object') {
                    // eslint-disable-next-line no-console
                    console.info(
                        '[RapidStor] Using dev app_config override from localStorage',
                    );
                    appConfigParsed = overrideJson as AppConfig;
                }
            }
        }
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[RapidStor] Failed to parse dev app_config override', e);
    }

    const options: RapidStorOptions = buildRapidStorOptions(
        appConfigParsed,
        persona,
        moveInUrl,
    );

    window.rapidstor_settings = {
        app_config: appConfigParsed,
        rapidstor_options: options,
    };
};

/**
* Application startup method, this is where we initialise Sentry and other global settings related to the app
*/
export const initApplication = async () => {
    const client = loggingClient();
    client.init();

    if (isDevelopment()) {
        // eslint-disable-next-line no-console
        console.info(
            `%cRapidStor Journey Based Listing [DEV BUILD - v${__APP_VERSION__}]\nSentry: Enabled\nEnvironment: Development`,
            'background: #444; color: #fff; font-size: 16px; text-align:left;padding:12px; display:block; border-radius:8px;',
        );
    } else {
        // eslint-disable-next-line no-console
        console.info(
            `%cRapidStor Journey Based Listing [PRODUCTION BUILD - v${__APP_VERSION__}]\nSentry: Enabled\nEnvironment: Production`,
            'background: #444; color: #fff; font-size: 16px; text-align:left;padding:12px; display:block; border-radius:8px;',
        );
    }

    await bootstrapApplicationConfig();
};

export const transform404Error = (error: string): string => `[DEV] ${error}`;
